import { useDispatch, useSelector } from "react-redux";
import { DAEMON_STATUS, ORDER_STATUS } from "../../../Utils/Daemon/HaaS/statusesConstant";
import { useCallback, useState } from "react";
import { searchDaemon } from "../../../Services/Redux/actionCreators/hostingActions";
import {
    setAiServiceGroups,
    setIsHaasUsed,
} from "../../../Services/Redux/actionCreators/aiServiceDetailsActions";
import {
    startAppLoader,
    stopAppLoader,
} from "../../../Services/Redux/actionCreators/loaderActions";

const TIMEOUT = 30000;

export const useDaemonStatus = () => {
    const dispatch = useDispatch();
    const serviceGroups = useSelector((state) => state.aiServiceDetails.groups);
    const [isPolling, setIsPolling] = useState(false);

    const searchDaemons = useCallback(
        async (orgId, serviceId) => {
            if (!orgId || !serviceId) return null;

            try {
                dispatch(startAppLoader({ title: "Searching daemon", content: "" }));
                const existDaemon = await dispatch(searchDaemon(orgId, serviceId));
                return existDaemon;
            } catch (error) {
                console.error("Error searching daemon:", error);
                return null;
            } finally {
                dispatch(stopAppLoader());
            }
        },
        [dispatch]
    );

    const isShouldPoll = useCallback(
        (existDaemon) =>
            existDaemon?.daemon?.status === DAEMON_STATUS.INIT &&
            existDaemon?.order?.status === ORDER_STATUS.PROCESSING,
        []
    );

    const pollDaemonStatus = useCallback(
        async (existDaemon, orgId, serviceId) => {
            if (!existDaemon) return;

            let shouldPoll = isShouldPoll(existDaemon);
            if (shouldPoll) {
                setIsPolling(true);
                dispatch(
                    startAppLoader({
                        title: "Processing…",
                        content:
                            "Your transaction is being recorded into the blockchain and then execution will proceed. Please wait.",
                    })
                );

                try {
                    let updatedDaemon;
                    while (shouldPoll) {
                        await new Promise((resolve) => setTimeout(resolve, TIMEOUT));
                        updatedDaemon = await dispatch(searchDaemon(orgId, serviceId));
                        shouldPoll = isShouldPoll(updatedDaemon);
                    }
                    return updatedDaemon;
                } finally {
                    setIsPolling(false);
                    dispatch(stopAppLoader());
                }
            }
        },
        [dispatch, isShouldPoll]
    );

    const processDaemonResult = useCallback(
        (existDaemon) => {
            dispatch(setIsHaasUsed(false));
            if (!existDaemon) return;

            if (
                existDaemon?.daemon?.daemonEndpoint &&
                existDaemon?.order?.status === ORDER_STATUS.SUCCESS
            ) {
                dispatch(setIsHaasUsed(true));
                dispatch(
                    setAiServiceGroups([
                        {
                            ...serviceGroups[0],
                            freeCallSignerAddress: process.env.REACT_APP_HAAS_FREECALL_ADDRESS,
                            endpoints: {
                                [existDaemon?.daemon?.daemonEndpoint]: { valid: true },
                            },
                        },
                        ...serviceGroups.slice(1),
                    ])
                );
            }

            if (
                existDaemon?.order?.status === ORDER_STATUS.FAILED &&
                existDaemon?.daemon?.status === DAEMON_STATUS.INIT
            ) {
                return existDaemon.order.id;
            }
        },
        [dispatch, serviceGroups]
    );

    return {
        searchDaemons,
        pollDaemonStatus,
        processDaemonResult,
        isPolling,
    };
};
